const inFlight = Object.create(null);

const state = () => ({
  cache: {}, 
});

const mutations = {
  SET_CACHE_ENTRY(state, { key, data }) {
    state.cache = { ...state.cache, [key]: { ts: Date.now(), data } };
  },
  CLEAR_CACHE_ENTRY(state, key) {
    if (!key) {
      state.cache = {};
    } else {
      const copy = { ...state.cache };
      delete copy[key];
      state.cache = copy;
    }
  },
};

const getters = {
  getCached: (state) => (key) => {
    return state.cache[key] ? state.cache[key].data : null;
  },
  getCacheMeta: (state) => (key) => {
    return state.cache[key] ? { ts: state.cache[key].ts } : null;
  },
};

const actions = {
  /**
   * Generic cached fetch action
   * payload: {
   *   serviceName: 'serviceName',    // optional label for logging
   *   keyParts: ['user123', '/api/v1'], // used to build the cache key
   *   apiCall: () => Promise,            // function that returns the API promise
   *   force: false,                      // bypass cache if true
   *   ttl: 0,                            // milliseconds; 0 => no ttl
   *   commitMutation: { name: 'SET_ACTION_VALUES', wrap: (res)=>... } // optional commit
   * }
   */
  async fetchCached({ state, commit }, payload) {
    const {
      serviceName = 'generic',
      keyParts = [],
      apiCall,
      force = false,
      ttl = 0,
      commitMutation = null,
    } = payload;

    if (typeof apiCall !== 'function') {
      return Promise.reject(new Error('fetchCached requires apiCall function'));
    }

    // CACHE KEY
    const key = `${serviceName}::${keyParts.map(p => String(p)).join('::')}`;

    // 1) check cache (memoize)
    const cached = state.cache[key];
    if (!force && cached) {
      if (!ttl || (Date.now() - cached.ts) <= ttl) {
        console.warn(`CACHE HIT - ${key}`);
        return Promise.resolve({ data: cached.data, fromCache: true });
      }
      // ttl expired -> fall through to refetch
    }

    // 2) check in-flight dedupe
    if (!force && inFlight[key]) {
      // return the existing in-flight promise
      return inFlight[key];
    }

    // 3) make the api call, store promise in inFlight
    const promise = (async () => {
      try {
        const res = await apiCall();
        const data = res && res.data !== undefined ? res.data : res;
        // console.warn(`${serviceName} - API HIT: `, data);

        // CACHE the result
        commit('SET_CACHE_ENTRY', { key, data });

        // OPTIONALLY: commit a service-specific mutation (e.g., SET_ACTION_VALUES)
        if (commitMutation && commitMutation.name) {
          const payloadForCommit = commitMutation.wrap
            ? commitMutation.wrap(data)
            : data;
          commit(commitMutation.name, payloadForCommit);
        }

        return { data, fromCache: false, key };
      } catch (err) {
        throw err;
      } finally {
        delete inFlight[key];
      }
    })();

    inFlight[key] = promise;
    return promise;
  },

  clearCache({ commit }, { serviceName = null, keyParts = null } = {}) {
    if (!serviceName) {
      commit('CLEAR_CACHE_ENTRY', null);
      return Promise.resolve();
    }
    const key = keyParts ? `${serviceName}::${keyParts.map(p => String(p)).join('::')}` : null;
    commit('CLEAR_CACHE_ENTRY', key);
    return Promise.resolve();
  }
};

export default {
  state,
  mutations,
  actions,
  getters
};
